<template>
  <DashboardLayout>
    <div class="max-w-3xl mx-auto">
      <!-- Header -->
      <div class="mb-6">
        <Link
          :href="route('settings.tickets.categories.index')"
          class="text-sm text-gray-600 hover:text-gray-900 flex items-center mb-4"
        >
          <ArrowLeftIcon class="h-4 w-4 mr-1" />
          Back to Categories
        </Link>
        <h1 class="text-3xl font-bold text-gray-900">
          {{ isEditMode ? 'Edit Category' : 'Create New Category' }}
        </h1>
        <p class="text-gray-600 mt-1">
          {{ isEditMode ? 'Update category information' : 'Add a new ticket category' }}
        </p>
      </div>

      <form @submit.prevent="submitForm">
        <div class="space-y-6">
          <!-- Basic Information -->
          <Card>
            <h2 class="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
            <div class="space-y-4">
              <!-- Name -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Category Name <span class="text-red-500">*</span>
                </label>
                <Input
                  v-model="form.name"
                  placeholder="e.g., Hardware Issues, Software Support"
                  :error="form.errors.name"
                  maxlength="100"
                />
                <p v-if="form.errors.name" class="mt-1 text-sm text-red-600">
                  {{ form.errors.name }}
                </p>
              </div>

              <!-- Parent Category -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Parent Category
                </label>
                <Select
                  v-model="form.parent_id"
                  :options="parentCategoryOptions"
                  placeholder="None (Top-level category)"
                  :error="form.errors.parent_id"
                />
                <p class="text-xs text-gray-500 mt-1">
                  Select a parent category to make this a subcategory
                </p>
                <p v-if="form.errors.parent_id" class="mt-1 text-sm text-red-600">
                  {{ form.errors.parent_id }}
                </p>
              </div>

              <!-- Description -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  v-model="form.description"
                  rows="3"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Brief description of this category..."
                  :class="{ 'border-red-500': form.errors.description }"
                ></textarea>
                <p v-if="form.errors.description" class="mt-1 text-sm text-red-600">
                  {{ form.errors.description }}
                </p>
              </div>
            </div>
          </Card>

          <!-- Appearance -->
          <Card>
            <h2 class="text-lg font-semibold text-gray-900 mb-4">Appearance</h2>
            <div class="space-y-4">
              <!-- Icon Selector -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Icon
                </label>
                <div class="grid grid-cols-6 md:grid-cols-10 gap-2">
                  <button
                    v-for="icon in iconOptions"
                    :key="icon.value"
                    type="button"
                    @click="form.icon = icon.value"
                    class="p-3 border-2 rounded-lg flex items-center justify-center transition"
                    :class="[
                      form.icon === icon.value
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    ]"
                    :title="icon.label"
                  >
                    <component :is="icon.component" class="h-5 w-5 text-gray-700" />
                  </button>
                </div>
              </div>

              <!-- Color Picker -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Color
                </label>
                <div class="flex items-center space-x-4">
                  <div class="flex space-x-2">
                    <button
                      v-for="color in colorOptions"
                      :key="color"
                      type="button"
                      @click="form.color = color"
                      class="w-10 h-10 rounded-lg border-2 transition"
                      :style="{ backgroundColor: color }"
                      :class="[
                        form.color === color
                          ? 'border-gray-900 scale-110'
                          : 'border-gray-300 hover:scale-105'
                      ]"
                    >
                      <CheckIcon
                        v-if="form.color === color"
                        class="h-5 w-5 text-white mx-auto"
                      />
                    </button>
                  </div>
                  <div class="flex items-center space-x-2">
                    <span class="text-sm text-gray-600">Custom:</span>
                    <input
                      v-model="form.color"
                      type="color"
                      class="h-10 w-16 rounded border border-gray-300 cursor-pointer"
                    />
                    <code class="text-xs text-gray-600 font-mono">{{ form.color }}</code>
                  </div>
                </div>
              </div>

              <!-- Preview -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Preview
                </label>
                <div class="flex items-center p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div
                    class="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center mr-3"
                    :style="{ backgroundColor: form.color || '#e5e7eb' }"
                  >
                    <component
                      v-if="form.icon"
                      :is="getIconComponent(form.icon)"
                      class="h-5 w-5 text-white"
                    />
                    <FolderIcon v-else class="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p class="text-sm font-semibold text-gray-900">
                      {{ form.name || 'Category Name' }}
                    </p>
                    <p class="text-xs text-gray-600">
                      {{ form.description || 'Category description' }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <!-- Defaults -->
          <Card>
            <h2 class="text-lg font-semibold text-gray-900 mb-4">Default Settings</h2>
            <div class="space-y-4">
              <!-- Default Priority -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Default Priority
                </label>
                <Select
                  v-model="form.default_priority"
                  :options="priorityOptions"
                  placeholder="None (User selects)"
                />
                <p class="text-xs text-gray-500 mt-1">
                  New tickets in this category will default to this priority
                </p>
              </div>

              <!-- Default Assignee -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Default Assignee
                </label>
                <Select
                  v-model="form.default_assignee_id"
                  :options="userOptions"
                  placeholder="None (Unassigned)"
                >
                  <template #selected v-if="form.default_assignee_id">
                    <div class="flex items-center space-x-2">
                      <Avatar
                        :src="getSelectedUser(form.default_assignee_id)?.profile_photo"
                        :name="getSelectedUser(form.default_assignee_id)?.full_name"
                        size="xs"
                      />
                      <span>{{ getSelectedUser(form.default_assignee_id)?.full_name }}</span>
                    </div>
                  </template>
                </Select>
                <p class="text-xs text-gray-500 mt-1">
                  New tickets in this category will be automatically assigned to this user
                </p>
              </div>

              <!-- SLA Policy -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  SLA Policy
                </label>
                <Select
                  v-model="form.sla_policy_id"
                  :options="slaPolicyOptions"
                  placeholder="None (Priority-based)"
                />
                <p class="text-xs text-gray-500 mt-1">
                  Override default SLA policy for tickets in this category
                </p>
              </div>
            </div>
          </Card>

          <!-- Status -->
          <Card>
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-sm font-medium text-gray-900">Active</h3>
                <p class="text-sm text-gray-500">
                  Inactive categories won't be available when creating tickets
                </p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  v-model="form.is_active"
                  type="checkbox"
                  class="sr-only peer"
                />
                <div
                  class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"
                ></div>
              </label>
            </div>
          </Card>

          <!-- Actions -->
          <Card>
            <div class="flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                @click="handleCancel"
              >
                Cancel
              </Button>
              <div class="flex space-x-3">
                <Button
                  v-if="isEditMode"
                  type="button"
                  variant="outline"
                  @click="showDeleteModal = true"
                  :disabled="form.processing"
                >
                  <TrashIcon class="h-4 w-4 mr-2" />
                  Delete
                </Button>
                <Button
                  type="submit"
                  :loading="form.processing"
                  :disabled="!isFormValid"
                >
                  <CheckIcon class="h-5 w-5 mr-2" />
                  {{ isEditMode ? 'Update Category' : 'Create Category' }}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </form>
    </div>

    <!-- Delete Confirmation Modal -->
    <Modal :show="showDeleteModal" @close="showDeleteModal = false" max-width="md">
      <template #title>Delete Category</template>
      <template #content>
        <p class="text-sm text-gray-600">
          Are you sure you want to delete this category? This action cannot be undone.
        </p>
      </template>
      <template #footer>
        <div class="flex justify-end space-x-3">
          <Button variant="outline" @click="showDeleteModal = false">Cancel</Button>
          <Button variant="danger" @click="deleteCategory" :loading="deleteLoading">
            Delete Category
          </Button>
        </div>
      </template>
    </Modal>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed } from 'vue';
import { router, Link, useForm } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Input from '@/Components/Common/Input.vue';
import Select from '@/Components/Common/Select.vue';
import Avatar from '@/Components/Common/Avatar.vue';
import Modal from '@/Components/Common/Modal.vue';
import {
  ArrowLeftIcon,
  CheckIcon,
  TrashIcon,
  FolderIcon,
  FolderOpenIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  ServerIcon,
  WifiIcon,
  CogIcon,
  ShieldCheckIcon,
  BugAntIcon,
  QuestionMarkCircleIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
} from '@heroicons/vue/24/outline';
import { useToast } from '@/Composables/useToast';

const props = defineProps({
  category: {
    type: Object,
    default: null,
  },
  categories: {
    type: Array,
    default: () => [],
  },
  users: {
    type: Array,
    default: () => [],
  },
  slaPolicies: {
    type: Array,
    default: () => [],
  },
  parentId: {
    type: Number,
    default: null,
  },
});

const { showToast } = useToast();

// State
const showDeleteModal = ref(false);
const deleteLoading = ref(false);

// Computed
const isEditMode = computed(() => !!props.category);

// Form
const form = useForm({
  name: props.category?.name || '',
  parent_id: props.category?.parent_id || props.parentId || null,
  description: props.category?.description || '',
  icon: props.category?.icon || 'folder',
  color: props.category?.color || '#3b82f6',
  default_priority: props.category?.default_priority || null,
  default_assignee_id: props.category?.default_assignee_id || null,
  sla_policy_id: props.category?.sla_policy_id || null,
  is_active: props.category?.is_active ?? true,
});

// Options
const iconOptions = [
  { value: 'folder', label: 'Folder', component: FolderIcon },
  { value: 'folder-open', label: 'Folder Open', component: FolderOpenIcon },
  { value: 'computer', label: 'Computer', component: ComputerDesktopIcon },
  { value: 'phone', label: 'Phone', component: DevicePhoneMobileIcon },
  { value: 'server', label: 'Server', component: ServerIcon },
  { value: 'wifi', label: 'WiFi', component: WifiIcon },
  { value: 'cog', label: 'Settings', component: CogIcon },
  { value: 'shield', label: 'Security', component: ShieldCheckIcon },
  { value: 'bug', label: 'Bug', component: BugAntIcon },
  { value: 'question', label: 'Help', component: QuestionMarkCircleIcon },
  { value: 'chat', label: 'Chat', component: ChatBubbleLeftRightIcon },
  { value: 'document', label: 'Document', component: DocumentTextIcon },
];

const colorOptions = [
  '#3b82f6', // Blue
  '#10b981', // Green
  '#f59e0b', // Amber
  '#ef4444', // Red
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#06b6d4', // Cyan
  '#84cc16', // Lime
];

const priorityOptions = [
  { value: null, label: 'None (User selects)' },
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const parentCategoryOptions = computed(() => {
  // Filter out current category and its children if editing
  let availableCategories = props.categories.filter(cat => !cat.parent_id);

  if (isEditMode.value) {
    availableCategories = availableCategories.filter(
      cat => cat.id !== props.category.id
    );
  }

  return [
    { value: null, label: 'None (Top-level category)' },
    ...availableCategories.map(cat => ({
      value: cat.id,
      label: cat.name,
    })),
  ];
});

const userOptions = computed(() => [
  { value: null, label: 'None (Unassigned)' },
  ...props.users.map(user => ({
    value: user.id,
    label: user.full_name,
  })),
]);

const slaPolicyOptions = computed(() => [
  { value: null, label: 'None (Priority-based)' },
  ...props.slaPolicies.map(policy => ({
    value: policy.id,
    label: policy.name,
  })),
]);

const isFormValid = computed(() => {
  return form.name.trim().length > 0;
});

// Methods
const getIconComponent = (iconValue) => {
  const icon = iconOptions.find(i => i.value === iconValue);
  return icon?.component || FolderIcon;
};

const getSelectedUser = (userId) => {
  return props.users.find(u => u.id === userId);
};

const submitForm = () => {
  const url = isEditMode.value
    ? route('settings.tickets.categories.update', props.category.id)
    : route('settings.tickets.categories.store');

  const method = isEditMode.value ? 'put' : 'post';

  form[method](url, {
    preserveState: true,
    onSuccess: () => {
      showToast({
        type: 'success',
        message: isEditMode.value
          ? 'Category updated successfully.'
          : 'Category created successfully.',
      });
      router.visit(route('settings.tickets.categories.index'));
    },
    onError: () => {
      showToast({
        type: 'error',
        message: 'Please fix the errors and try again.',
      });
    },
  });
};

const deleteCategory = () => {
  if (!props.category) return;

  deleteLoading.value = true;

  router.delete(route('settings.tickets.categories.destroy', props.category.id), {
    onSuccess: () => {
      showToast({
        type: 'success',
        message: 'Category deleted successfully.',
      });
      router.visit(route('settings.tickets.categories.index'));
    },
    onError: () => {
      showToast({
        type: 'error',
        message: 'Failed to delete category.',
      });
      deleteLoading.value = false;
    },
  });
};

const handleCancel = () => {
  router.visit(route('settings.tickets.categories.index'));
};
</script>
